import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Typography,
  InfoItem,
  Button,
  ImageGallery,
  ImageGalleryCard,
  BorderLine,
  EmptyFilesMessage,
  fullScreenButtonClick,
  repairSetUpdateMultipleModal,
  enqueueSnackbar,
  HideInput,
  FileInput,
} from '../../design-system';
import {
  DashboardFormWrapper,
  DashboardHeader,
  DashboardFormCards,
  DashboardFormCard,
  DashboardFormCustomColumn,
  DashboardFooterWithResize,
} from '../dashboard';
import { useStore, useStoreUser } from '../../context';
import { filesFormatValidator, getFilesChanges, momentTextFormat, showToRoles } from '../../utils';
import { CURRENCIES, INPUT_ERRORS, MESSAGES, RepairStatusToChipLabels } from '../../const';
import { RepairGetAllQuery, RepairStatus } from '../../types';
import { RepairsPage } from './RepairsComponents';
import { i18n } from '../../i18n';
import { useUpdateImagesRepairMutation } from '../../hooks';

export const RepairsViewPage = styled(RepairsPage)`
  grid-template-rows: initial;
  grid-auto-rows: max-content;
`;

interface RepairsViewHeaderProps {
  repair: RepairGetAllQuery['data'][number];
  containerId?: string;
  onGoBack?: () => void;
}

export const RepairsViewHeader = ({ repair, containerId, onGoBack }: RepairsViewHeaderProps) => {
  const { user } = useStoreUser();

  const navigate = useNavigate();
  const { id: routeContainerId } = useParams();
  const effectiveContainerId = containerId || routeContainerId || '';

  const handleAddRepair = () => {
    repairSetUpdateMultipleModal.showContainerSelect = false;
    repairSetUpdateMultipleModal.containerId = effectiveContainerId;
    repairSetUpdateMultipleModal.action = 'set-price';
    repairSetUpdateMultipleModal.number = repair.container.number;

    repairSetUpdateMultipleModal.data = [
      {
        id: effectiveContainerId,
        price: '0',
        expectedDurationDays: 0,
        description: '',
        beforeImages: [],
      },
    ];

    repairSetUpdateMultipleModal.submitClick = () => {
      enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.REPAIR, 'success');
      navigate(0);
    };
    repairSetUpdateMultipleModal.open();
  };

  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack();
      return;
    }

    navigate(-1);
  };

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {repair?.container?.number
          ? `${i18n.t('pages.modal.repairTitle')}: ${repair.container.number}`
          : ''}
      </Typography>
      <Button size={'medium'} data-color={'white'} onClick={handleGoBack}>
        {i18n.t('button.goBack')}
      </Button>
      {showToRoles('admin', user) && (
        <Button size={'medium'} data-color={'primary'} onClick={handleAddRepair}>
          {i18n.t('button.addRepair')}
        </Button>
      )}
    </DashboardHeader>
  );
};

export const RepairFormCards = ({ children }: any) => {
  return (
    <DashboardFormWrapper flow={'row'}>
      <DashboardFormCards id={'repairs-view'}>{children}</DashboardFormCards>
    </DashboardFormWrapper>
  );
};

export const RepairsViewTopCards = ({ repair }: { repair: RepairGetAllQuery['data'][number] }) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  return (
    <>
      <DashboardFormCard paperSize={'full-size'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('common.generalInformation')}
        </Typography>
        <InfoItem label={i18n.t('label.containerNumber')} value={repair.container.number} />
        <InfoItem label={i18n.t('common.client')} value={repair.container.clientYard.client.code} />
        <InfoItem
          label={i18n.t('label.acceptedAt')}
          value={repair.acceptedAt && momentTextFormat(repair.acceptedAt, user)}
        />
        <InfoItem label={i18n.t('label.priceSetBy')} value={repair.completedByName} />
        <InfoItem
          label={i18n.t('label.price')}
          value={repair.price ? repair.price + CURRENCIES[repair.currencyCode].symbol : ''}
        />
      </DashboardFormCard>
      <DashboardFormCard paperSize={'full-size'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('pages.repairs.titleInformation')}
        </Typography>
        <InfoItem
          label={i18n.t('label.repairStatus')}
          value={RepairStatusToChipLabels[repair.status]}
          capitalized
        />
        <InfoItem
          label={i18n.t('label.daysToComplete')}
          value={String(repair.expectedDurationDays || '')}
        />
        <Typography variant={'textM'} color={'--gray-90'}>
          {repair.description}
        </Typography>
      </DashboardFormCard>
    </>
  );
};

interface RepairsViewImagesProps {
  title: string;
  images: RepairGetAllQuery['data'][number]['beforeImages' | 'afterImages'];
  repairId: string;
  repairStatus: RepairStatus;
  imageCategory: 'before' | 'after';
  onImagesUpdated?: (
    images: RepairGetAllQuery['data'][number]['beforeImages' | 'afterImages'],
  ) => void;
}

const editableRepairStatuses: RepairStatus[] = ['DRAFT', 'PENDING', 'ACTIVE'];

export const RepairsViewImages = ({
  title,
  images,
  repairId,
  repairStatus,
  imageCategory,
  onImagesUpdated,
}: RepairsViewImagesProps) => {
  const { user } = useStoreUser();

  const updateImagesMutation = useUpdateImagesRepairMutation();
  const canEditImages = editableRepairStatuses.includes(repairStatus) && showToRoles('admin', user);

  const [photos, setPhotos] = useState<any[]>([]);
  const photosFiles = useRef<any[]>([]);
  const [oldPhotos, setOldPhotos] = useState(images || []);
  const deletePhotos = useRef<any[]>([]);
  const photosInputRef = useRef<any>();
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [photosLength, setPhotosLength] = useState(0);
  const [isDeletePhotos, setIsDeletePhotos] = useState(false);

  const resetPendingChanges = (nextOldPhotos = images || []) => {
    photosFiles.current = [];
    deletePhotos.current = [];
    setPhotos([]);
    setOldPhotos(nextOldPhotos);
    setPhotosLength(0);
    setIsDeletePhotos(false);
  };

  useEffect(() => {
    resetPendingChanges(images || []);
  }, [repairId]);

  const handleChangePhotos = (newImages: any, files: any) => {
    const newPhotosValues = [...photos, ...newImages];

    if (filesFormatValidator(files, 'Image')) {
      enqueueSnackbar(filesFormatValidator(files, 'Image'), 'warning');
      return;
    }

    setPhotos(newPhotosValues);
    photosFiles.current = [...photosFiles.current, ...files];
    setPhotosLength(newPhotosValues.length);
    photosInputRef.current.type = 'text';
    photosInputRef.current.type = 'file';
  };

  const handleZoomPhotoClick = (i: number) => {
    setGalleryImages([...oldPhotos.map(({ url }: any) => url), ...photos]);
    setZoomImageIndex(i);
    fullScreenButtonClick('repair-view-images' + title.split(' ').join(''));
  };

  const handleDeletePhotoClick = (i: number) => {
    if (i - oldPhotos.length >= 0) {
      i = i - oldPhotos.length;
      photosFiles.current = photosFiles.current.filter((_: any, index: number) => index !== i);
      setPhotos(photos.filter((_: any, index: number) => index !== i));
      setPhotosLength(photos.length - 1);
    } else {
      deletePhotos.current = [
        ...deletePhotos.current,
        { action: 'delete', name: oldPhotos[i].name },
      ];

      setOldPhotos(oldPhotos.filter((_: any, index: number) => index !== i));
      setIsDeletePhotos(true);
    }
  };

  const handleCancelClick = () => {
    resetPendingChanges(images || []);
  };

  const handleSubmitClick = async () => {
    if (photos.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    const changedPhotos = getFilesChanges(photos, photosFiles.current, deletePhotos.current);

    if (!changedPhotos.length) return;

    await updateImagesMutation.mutate(
      {
        id: repairId,
        photos: changedPhotos,
        action: imageCategory,
      },
      {
        onSuccess: () => {
          const updatedImages = [
            ...oldPhotos,
            ...photos.map((file: any, i: number) => ({
              name: photosFiles.current[i]?.name || '',
              url: typeof file === 'string' ? file : file,
            })),
          ];

          enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.FILES_CHANGE, 'success');
          photosFiles.current = [];
          deletePhotos.current = [];
          setOldPhotos(updatedImages);
          setPhotos([]);
          setPhotosLength(0);
          setIsDeletePhotos(false);
          onImagesUpdated?.(updatedImages);
        },
      },
    );
  };

  const displayedImages = canEditImages ? [...oldPhotos, ...photos] : images;
  const galleryId = 'repair-view-images' + title.split(' ').join('');

  return (
    <>
      <ImageGallery id={galleryId} photos={galleryImages} startIndex={zoomImageIndex} />
      <DashboardFormCard paperSize={'full-size'} rows={'max-content max-content 1fr'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {title}
        </Typography>
        <BorderLine space={'dashboard-form-card'} />
        <DashboardFormCustomColumn
          columns={'1fr 1fr 1fr'}
          alignContent={!displayedImages.length ? 'center' : 'initial'}
        >
          {!displayedImages.length && (
            <EmptyFilesMessage variant={'textM'} align={'center'} gridColumn={'1 / 4'}>
              {i18n.t('pages.repairs.noImages')}
            </EmptyFilesMessage>
          )}
          {displayedImages.map((img, i) => {
            const imageUrl = typeof img === 'string' ? img : img.url;
            const hideDeleteButton =
              !canEditImages || (imageCategory === 'before' && displayedImages.length <= 1);

            return (
              <ImageGalleryCard
                key={i}
                img={imageUrl}
                handleZoomClick={() => handleZoomPhotoClick(i)}
                handleDeleteImageClick={() => handleDeletePhotoClick(i)}
                hideDeleteButton={hideDeleteButton}
              />
            );
          })}
          {canEditImages && (
            <HideInput>
              <FileInput
                label={i18n.t('button.addImages')}
                inputRef={photosInputRef}
                inputProps={{
                  accept: '.jpeg, .jpg, .png, .webp',
                  multiple: true,
                }}
                handleChangeFiles={handleChangePhotos}
              />
            </HideInput>
          )}
        </DashboardFormCustomColumn>
        {canEditImages && (
          <>
            <BorderLine space={'dashboard-form-card-around'} />
            <DashboardFooterWithResize columns={'max-content 1fr'}>
              <Button
                size={'medium'}
                data-color={'primary'}
                onClick={() => photosInputRef.current.click()}
                justify={'left'}
              >
                {i18n.t('button.addImages')}
              </Button>
              <span />
              <Button
                size={'medium'}
                data-color={'white'}
                onClick={handleCancelClick}
                disabled={!photosLength && !isDeletePhotos}
              >
                {i18n.t('button.cancel')}
              </Button>
              <Button
                size={'medium'}
                data-color={'primary'}
                onClick={handleSubmitClick}
                disabled={!photosLength && !isDeletePhotos}
              >
                {i18n.t('button.save')}
              </Button>
            </DashboardFooterWithResize>
          </>
        )}
      </DashboardFormCard>
    </>
  );
};
